import { ApolloError, useMutation } from "@apollo/client";
import cx from "classnames";
import { useCallback, useMemo, useState } from "react";

import CodeEditor from "components/CodeEditor";
import DocumentationIconButton from "components/DocumentationIconButton";
import { Cross, Expand } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Link from "ds/components/Link";
import Select from "ds/components/Select";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";
import { EMPTY_POLICY_SAMPLE_RESULTS_MSG } from "shared/Policy/constants";
import { SIMULATE_POLICY_V2 } from "shared/Policy/gql";
import { PolicyEngineType, PolicyEvaluationSample, RunPolicyReceipt } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { beautifyCode } from "utils/strings";
import FlashContext from "components/FlashMessages/FlashContext";

import styles from "./styles.module.css";

type RunSimulationPanelProps = {
  sample: PolicyEvaluationSample;
  policyReceipt: RunPolicyReceipt;
};

const RunSimulationPanel = (props: RunSimulationPanelProps) => {
  const { sample, policyReceipt } = props;
  const { onError } = useTypedContext(FlashContext);

  const [sampleBody, setSampleBody] = useState(sample.body);
  const [sampleInput, setSampleInput] = useState(beautifyCode(sample.input));
  const [simulationResult, setSimulationResult] = useState(EMPTY_POLICY_SAMPLE_RESULTS_MSG);
  const [isFullScreen, setFullScreen] = useState(false);
  const [policyEngineType, setPolicyEngineType] = useState<PolicyEngineType>(
    policyReceipt.engineType
  );

  const handleError = useCallback(
    (error: ApolloError) => {
      onError(error);
      setSimulationResult(EMPTY_POLICY_SAMPLE_RESULTS_MSG);
    },
    [onError]
  );

  const [simulatePolicyV2, { loading: simulateLoading }] = useMutation(SIMULATE_POLICY_V2, {
    variables: {
      input: {
        body: sampleBody,
        input: sampleInput,
        type: policyReceipt.policyType,
        engineType: policyEngineType,
        libraries: null,
      },
    },
    onError: handleError,
    onCompleted: (data) => {
      // eslint-disable-next-line @cspell/spellchecker
      setSimulationResult(beautifyCode(data.policySimulatev2));
    },
  });

  const [isPolicyBodyChanged, isSampleInputChanged] = useMemo(
    () => [sampleBody !== sample.body, sampleInput !== beautifyCode(sample.input || "")],
    [sample.body, sample.input, sampleBody, sampleInput]
  );

  const handleResetBody = useCallback(() => {
    if (isPolicyBodyChanged) {
      setSampleBody(sample.body);
    }
  }, [sample, isPolicyBodyChanged]);

  const handleResetInput = useCallback(() => {
    if (isSampleInputChanged) {
      setSampleInput(beautifyCode(sample.input));
    }
  }, [sample.input, isSampleInputChanged]);

  const handleSimulation = () => simulatePolicyV2();

  const handleBodyEditorChange = (value?: string) => {
    setSampleBody(value || "");
  };

  const handleInputEditorChange = (value?: string) => {
    setSampleInput(value || "");
  };

  return (
    <Box
      direction="column"
      className={cx(styles.wrapper, isFullScreen && styles.fullScreen)}
      fullWidth
    >
      <Box justify="between" align="center" className={styles.header}>
        <Typography variant={isFullScreen ? "p-t3" : "p-t7"} tag="h3" color="primary">
          Simulation panel
        </Typography>

        <Box gap="medium">
          <Select
            value={policyEngineType}
            size="small"
            items={[
              { label: "Rego V0", value: PolicyEngineType.RegoV0 },
              { label: "Rego V1", value: PolicyEngineType.RegoV1 },
            ]}
            onChange={(value: PolicyEngineType) => setPolicyEngineType(value)}
          />
          {isFullScreen ? (
            <ButtonIcon icon={Cross} onPress={() => setFullScreen(!isFullScreen)} variant="ghost">
              Close
            </ButtonIcon>
          ) : (
            <ButtonIcon
              variant="secondary"
              size="small"
              icon={Expand}
              onPress={() => setFullScreen(!isFullScreen)}
            >
              Expand
            </ButtonIcon>
          )}

          {!isFullScreen && (
            <Button
              variant="contrast"
              size={isFullScreen ? "medium" : "small"}
              onPress={handleSimulation}
              loading={simulateLoading}
            >
              Simulate
            </Button>
          )}
        </Box>
      </Box>
      <Box grid gridTemplate="50% 50%" fullWidth grow="1">
        <Box direction="column" className={styles.bodyEditorWrapper} shrink="1">
          <Box padding="large" gap="medium" className={styles.editorHeader}>
            <Typography variant="p-t7" tag="h4">
              Policy body
            </Typography>
            {isPolicyBodyChanged && (
              <Link onPress={handleResetBody} size="small">
                Reset
              </Link>
            )}
          </Box>
          <Box>
            <CodeEditor
              className={styles.bodyEditor}
              body={sampleBody}
              onChange={handleBodyEditorChange}
              language="rego"
            />
          </Box>
        </Box>
        <Box direction="column" shrink="1">
          <Box direction="column" className={styles.inputWrapper}>
            <Box padding="large" gap="medium" className={styles.editorHeader}>
              <Typography variant="p-t7" tag="h4">
                Input
              </Typography>
              {isSampleInputChanged && (
                <Link onPress={handleResetInput} size="small">
                  Reset
                </Link>
              )}
            </Box>
            <Box grow="1">
              <CodeEditor
                className={styles.editor}
                body={sampleInput}
                onChange={handleInputEditorChange}
                language="json"
              />
            </Box>
          </Box>
          <Box direction="column" className={styles.outputWrapper}>
            <Box padding="large" gap="medium" className={styles.editorHeader}>
              <Typography variant="p-t7" tag="h4">
                Output
              </Typography>
            </Box>
            <CodeEditor
              className={styles.simulationResult}
              body={simulationResult}
              language="json"
            />
          </Box>
        </Box>
      </Box>
      {isFullScreen && (
        <FullScreenModalFooter justify="between">
          <DocumentationIconButton
            href={getDocsUrl("/concepts/policy#policy-workbench")}
            tooltipText="Go to policies workbench documentation"
          />
          <Button
            variant="contrast"
            size={isFullScreen ? "medium" : "small"}
            onPress={handleSimulation}
            loading={simulateLoading}
          >
            Simulate
          </Button>
        </FullScreenModalFooter>
      )}
    </Box>
  );
};

export default RunSimulationPanel;
